import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import format from 'date-fns/format';

import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, Spin, Tooltip, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { useHistory } from 'react-router-dom';

import { DateSearchTypeEnum, IAccount, ICondoLocation, ISessionKey, IUser, IWeek } from '@share/common-types';
import { RefundableEnum } from '@common-types';
import { FunctionalRootState, renderNumber, getSelectedCurrency, LoginType } from '@share/utils';
import { HasPermission, ViewQuotesSharePermission, openCondoPage } from '@utils';
import { Currency, ModalShare } from '@components';
import { BlueButton } from '@share/components';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import { DownLoadShareType, HandleDownload } from '@share/api';

import './style.scss';

const modalWidth = 900;
const percents = 100;
const ONE = 1;
const DATE_FORMAT = 'd MMM';

export interface ModalCompareProps {
  visible: boolean;
  date?: string;
  period?: string;
  locationObject: ICondoLocation;
  sessionKey: ISessionKey;
  weeks: IWeek[];
  onCancel: () => void;
  onRemove: (week: IWeek) => void;
}

export function ModalCompare({
  sessionKey,
  locationObject,
  visible,
  weeks,
  date,
  period,
  onCancel,
  onRemove,
}: ModalCompareProps) {
  const intl = useIntl();
  const history = useHistory();

  const marginatorStore = useSelector((state: FunctionalRootState) => state.marginatorStore);
  const { account, user } = useSelector((state: FunctionalRootState) => state.loginStore);

  const [loading, setLoading] = useState(false);
  const [visibleAction, setVisibleAction] = useState(false);
  const [isShare, setIsShare] = useState(false);

  const { value } = marginatorStore;

  const currency = useMemo(() => getSelectedCurrency(account as IAccount), [account]);

  const handleView = (week: IWeek) => {
    openCondoPage(week.propertyId, history);
  };

  const handleData = useCallback(
    (members?: string[], comment?: string) => {
      const data: Record<string, unknown> = {
        quotes: weeks.map((week) => {
          const marginatorPrice = (week?.price ? week?.price : 0) / (1 - (value / 100));
          const savingsNunmber = week.savings / marginatorPrice * 100;
          const discount = savingsNunmber ? Math.floor(savingsNunmber) : 0;

          return {
            condoId: week.propertyId,
            displayName: week.displayName,
            fullAddress: `${week.address}${!isEmpty(week?.state) ? `, ${week?.state}` : ''}`,
            refundability: week.refundability,
            reviewsCount: week.rating?.reviewsCount,
            rating: week.rating?.value,
            currency,
            price: marginatorPrice,
            publicPrice: week.publicPrice,
            supplierType: week.supplierType,
            availableDates: week.availableDates?.length
              ? [
                  ...week.availableDates?.map((ad) => ({
                    matchType: ad.matchType,
                    totalNights: moment(ad.to, 'yyyy-MM-DD').diff(moment(ad.from, 'yyyy-MM-DD'), 'days'),
                    from: ad.from,
                    to: ad.to,
                  })),
                ]
              : [],
            discount: discount.toString(),
            image: get(week, 'images[0].url'),
          };
        }),
        comment,
        members,
        location: locationObject,
        searchType: DateSearchTypeEnum.Strict,
        propertyRequest: {
          location: locationObject,
        },
        sessionKey,
      };

      (data.propertyRequest as Record<string, unknown>).checkIn = moment(date, 'MM/DD/yyyy').format('yyyy-MM-DD');
      (data.propertyRequest as Record<string, unknown>).dateRange = period;
      (data.propertyRequest as Record<string, unknown>).residency = 'US';

      return data;
    },
    [weeks, value, currency, locationObject, sessionKey, date, period],
  );

  const handleDownload = () => {
    const prevLoading = loading;
    setLoading(true);
    HandleDownload(DownLoadShareType.WEEK, prevLoading, handleData(), intl, () => setLoading(false), () =>
      setVisibleAction(false),
    );
  };

  const fontFamily = account?.fontFamily;
  const secondaryFontFamily = account?.secondaryFontFamily;

  return (
    <Modal
      className={`condo-modal-compare ${fontFamily ? `${fontFamily}-font` : ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font` : ''}`}
      open={visible}
      footer={null}
      onCancel={onCancel}
      wrapClassName="condo-modal-compare__wrapper"
      width={modalWidth}
    >
      <div>
        <div className="condo-modal-compare__title-row">
          <h4 className="condo-modal-compare__title secondary-font">
            <FormattedMessage id="results.condos.card.compare.modal.title" />
          </h4>
        </div>

        <div className="condo-modal-compare__hotels condo-modal-compare__hotels--meta">
          <div className="hotels-dates">
            <FormattedMessage id="specific.dates" /> - {moment(date, 'MM/DD/yyyy').format(DATE_FORMAT_CHECK_IN_OUT)}
          </div>
          <div className="hotels-dates">
            <FormattedMessage id="within" /> - <FormattedMessage id="days" values={{ count: period }} />
          </div>
        </div>

        <div className="condo-modal-compare__hotels">
          {weeks.map((week, index) => {
            const { price, availableDates } = week;

            const marginatorValue = marginatorStore.value || 0;
            const marginatorPrice = (price ? price : 0) / (1 - marginatorValue / 100);

            const msave = week.publicPrice ? (marginatorPrice * percents) / week.publicPrice : 100;
            const savings = Math.floor(percents - msave);

            return (
              <div key={index} className="condo-modal-compare__hotel">
                <div className="condo-modal-compare__media">
                  <div className="condo-images-container">
                    <div className="condo-image" style={{ backgroundImage: `url(${get(week, 'images[0].url')})` }} />
                    {savings > 0 ? (
                      <div className="condo-modal-compare__savings-badge">
                        {savings.toFixed(0)} % <FormattedMessage id="results.hotel.card.compare.modal.off" />
                      </div>
                    ) : null}
                  </div>
                  <label
                    className={`condo-modal-compare__remove-btn ${weeks.length <= 2 ? 'disabled' : ''}`}
                    onClick={() => {
                      if (weeks.length > 2) {
                        onRemove(week);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleXmark} className="condo-modal-compare__remove-btn-icon" />
                  </label>
                </div>

                <div className="condo-text-container condo-text-container--title">
                  <Tooltip title={week.displayName} placement="top">
                    <p>{week.displayName}</p>
                  </Tooltip>
                </div>

                <div className="condo-text-container condo-text-container--address">
                  <Tooltip
                    title={`${week.address}${!isEmpty(week?.state) ? `, ${week?.state}` : ''}`}
                    placement="top"
                  >
                    <p>{`${week.address}${!isEmpty(week?.state) ? `, ${week?.state}` : ''}`}</p>
                  </Tooltip>
                </div>

                <div className="condo-text-container" style={{ height: '40px' }}>
                  {availableDates?.length ? (
                    <div className="condo-modal-compare__date-label">
                      {availableDates.length >= ONE && (
                        <div className="condo-modal-compare__date-more">
                          <p className="condo-modal-compare__date-more-text">
                            <Tooltip
                              className="condo-compare-tooltip"
                              overlayStyle={{ whiteSpace: 'pre-line' }}
                              placement="top"
                              title={
                                <>
                                  {availableDates.map((d, i) => (
                                    <Typography key={i} style={{ color: '#FFFFFF' }}>
                                      {format(new Date(d.from), DATE_FORMAT)} - {format(new Date(d.to), DATE_FORMAT)}
                                    </Typography>
                                  ))}
                                </>
                              }
                            >
                              {availableDates.length} <FormattedMessage id="available.dates" />
                            </Tooltip>
                          </p>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                {week.refundability && week.refundability !== RefundableEnum.Unknown ? (
                  <div className="condo-text-container condo-text-container--refundable">
                    <div className="hotel-card-wrapper__refundable">
                      {week.refundability === RefundableEnum.Refundable && (
                        <div className="hotel-card-wrapper__refundable-ref">
                          <FormattedMessage id="refundable" />
                        </div>
                      )}
                      {week.refundability === RefundableEnum.Nonrefundable && (
                        <div className="hotel-card-wrapper__refundable-nonref">
                          <FormattedMessage id="non.refundable" />
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="condo-amenities-container" style={{ height: '43px' }}>
                  {week.rating?.value || week.rating?.reviewsCount ? (
                    <label>
                      {week.rating?.value ? (
                        <>
                          <strong>{week.rating?.value.toFixed(1)}</strong> / 5
                        </>
                      ) : null}{' '}
                      {week.rating?.reviewsCount ? (
                        <>
                          ({week.rating?.reviewsCount} <FormattedMessage id="reviews" />)
                        </>
                      ) : null}
                    </label>
                  ) : (
                    <label>
                      <FormattedMessage id="reviews.guest.no.ratings" />
                    </label>
                  )}
                </div>

                <div className="condo-text-container condo-text-container--price">
                  {week.publicPrice ? (
                    <p style={{ textDecoration: 'line-through' }}>
                      <Currency currency={currency} />
                      {renderNumber(week.publicPrice, 2)}
                    </p>
                  ) : null}
                  <p>
                    <strong>
                      <Currency currency={currency} />
                      {renderNumber(marginatorPrice, 2)}
                    </strong>
                  </p>
                </div>

                <div className="condo-view-container">
                  <div className="view-button" onClick={() => handleView(week)}>
                    <FormattedMessage id="results.hotel.card.compare.modal.view" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="condo-modal-compare__buttons">
          {!account || account.type !== LoginType.PrivateWithToken ? (
            account?.displayShareCompareCondos && (
              <BlueButton disabled={loading} onClick={handleDownload}>
                {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.download" />}
              </BlueButton>
            )
          ) : (
            <>
              {account?.displayShareCompareCondos && (
                <BlueButton
                  disabled={loading}
                  onClick={() => {
                    setVisibleAction(true);
                    setIsShare(false);
                  }}
                >
                  {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.download" />}
                </BlueButton>
              )}

              {HasPermission(user as IUser, ViewQuotesSharePermission) ? (
                <BlueButton
                  disabled={loading}
                  onClick={() => {
                    setVisibleAction(true);
                    setIsShare(true);
                  }}
                >
                  {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.share" />}
                </BlueButton>
              ) : null}
            </>
          )}
        </div>
      </div>

      <ModalShare
        visible={visibleAction}
        isShare={isShare}
        type={DownLoadShareType.WEEK}
        onCancel={() => setVisibleAction(false)}
        onLoading={(v) => setLoading(v)}
        convertData={handleData}
      />
    </Modal>
  );
}
